import { QueryClient } from '@tanstack/react-query';

import { timetableQueryKey } from './useTimetable.query';

/**
 * 同じダイヤへの保存（名前・所属の PATCH）を同期的に 1 つに絞る。courseSaveGate と同じ形で、送る前に取り、Promise の finally で解放する。
 * Why not キュー: 操作ごとに全件の所属を送るので、保存中の操作を後で送ると古い所属から組み立てた値で上書きする。待たせず捨てて知らせる
 */
const inFlight = new Set<number>();
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

export const tryAcquireTimetableSave = (timetableId: number): boolean => {
  if (inFlight.has(timetableId)) return false;
  inFlight.add(timetableId);
  notify();
  return true;
};
export const releaseTimetableSave = (timetableId: number) => {
  if (inFlight.delete(timetableId)) notify();
};
/**
 * ゲートが取られているか（保存中、または失敗後の取り直し中）。画面は購読して操作を止める。
 * Why not 画面の state: 取り直しの途中で詳細を開き直すと消え、押せる見た目のまま「保存中です」で断られる
 */
export const isTimetableSaveLocked = (timetableId: number) => inFlight.has(timetableId);

/**
 * 保存に失敗した後、詳細を取り直せなかったダイヤ。手元の所属が古いかもしれないので、読み込み直すまで操作を止める。
 * Why not 画面の state: 詳細を閉じて開き直すと忘れ、古い所属から次の保存を送れてしまう
 */
const stale = new Set<number>();

export const markTimetableStale = (timetableId: number) => { stale.add(timetableId); notify(); };
export const clearTimetableStale = (timetableId: number) => {
  if (stale.delete(timetableId)) notify();
};
export const isTimetableStale = (timetableId: number) => stale.has(timetableId);
/** ゲートと要読み込み直しの変化を購読する */
export const subscribeTimetableSaveGate = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

/**
 * ダイヤ詳細を、表示していなくても取り直す。取れたかを返し、取れなければ要読み込み直しを立てる。
 * 取れたかは dataUpdateCount が増えたかで見る。refetchQueries は throwOnError でも、オフラインで止まった取得は待たずに resolve する。
 * Why not dataUpdatedAt: 同じミリ秒の取得と見分けられない
 */
export const reloadTimetableDetail = async (queryClient: QueryClient, timetableId: number): Promise<boolean> => {
  const key = timetableQueryKey(timetableId);
  const before = queryClient.getQueryState(key);
  let ok: boolean;
  try {
    await queryClient.refetchQueries({ queryKey: key, type: 'all' }, { throwOnError: true });
    const after = queryClient.getQueryState(key);
    // キャッシュが無ければ古い値も無い。次に開くときに取り直される
    ok = !before || !after || after.dataUpdateCount > before.dataUpdateCount;
  } catch {
    ok = false;
  }
  if (ok) clearTimetableStale(timetableId);
  else markTimetableStale(timetableId);
  return ok;
};

/** テスト用 */
export const resetTimetableSaveGate = () => {
  inFlight.clear();
  stale.clear();
  notify();
};
